import React, { FC, useEffect } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import styled from 'styled-components';

import { Banner, BannerButton } from '../../../types/banner';
import { ItemLinkType } from '../../../constants';
import { simpleAppQuery } from '../../../queries';
import { SimpleApp } from '../../../types/app';
import { fontBold, fontSemibold } from '../../../themes';
import { WhiteGreyStarGroup } from '../Rating/Stars';
import { RowButton } from '../Button/RowButton';
import { BannerLink } from './BannerLink';

interface NewContentProps {
  banner: Banner;
  onClick: () => void;
}

const Container = styled.div`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  flex-direction: column;
`;

const Text = styled.div`
  color: white;
  padding-top: 10px;
`;

const Ratings = styled(Text)`
  padding-right: 5px;
  padding-top: 1px;
`;

const BannerContainer = styled.div`
  padding: 0 20px;
  text-align: center;
  display: flex;
  flex-grow: 1;
`;

const BannerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const RatingsContainer = styled.div`
  display: flex;
  padding: 10px;
  align-self: flex-end;
  margin-right: 20px;
`;

const BannerTitle = styled(Text)`
  font-family: ${fontBold};
`;

const BannerText = styled(Text)`
`;

const CustomRowButton = styled(RowButton)`
  padding: 5px 30px;
  text-transform: uppercase;
  font-family: ${fontSemibold};
  margin-top: 15px;
`;

interface BannerButtonProps {
  text: string;
}

const BannerButton: FC<BannerButtonProps> = ({ text }) => (
  <CustomRowButton bgColor="white" color="#112530">{text}</CustomRowButton>
)

interface AppContentProps {
  id: string;
  banner: Banner;
  onClick: () => void;
}

const AppContent: FC<AppContentProps> = ({ id, banner, onClick }) => {
  const variables = { id }
  const [getApp, { loading, data }] = useLazyQuery(simpleAppQuery, { variables });

  useEffect(() => {
    getApp();
  }, [getApp]);

  if (loading) {
    return <span/>;
  }

  if (!data || !data.app) {
    return <span/>;
  }

  const app: SimpleApp = data.app;

  if (!app) {
    return <span/>
  }

  return (
    <Container>
      <BannerContent
        banner={banner}
        onClick={onClick}
      />
      <RatingsContainer>
        <Ratings>{app.ratingSummary && app.ratingSummary.averageStars && app.ratingSummary.averageStars.toFixed(1)}</Ratings>
        <div>
          {app.ratingSummary && <WhiteGreyStarGroup filledStars={app.ratingSummary.averageStars} />}
        </div>
      </RatingsContainer>
    </Container>
  );
};

interface BannerContentProps {
  banner: Banner;
  onClick: () => void;
}

const BannerContent: FC<BannerContentProps> = ({
  banner: { title, text, button, isPremium },
  onClick
}) => {
  return (
    <BannerContainer>
      <BannerWrapper>
        {isPremium && <Text>premium</Text>}
        <BannerTitle>{title}</BannerTitle>
        <BannerText>{text}</BannerText>
        <BannerLink link={button.link} onClick={onClick}>
          <BannerButton text={button.text} />
        </BannerLink>
      </BannerWrapper>
    </BannerContainer>
  );
}

export const Content: FC<NewContentProps> = ({ banner, onClick }) => {
  const { button } = banner;
  const isApp = button.link.type === ItemLinkType.APP;

  if (isApp) {
    return (
      <AppContent id={button.link.resourceId} banner={banner} onClick={onClick} />
    );
  }

  return (
    <Container>
      <BannerContent
        banner={banner}
        onClick={onClick}
      />
    </Container>
  );
};